import React from "react";
import PropTypes from "prop-types";
import { Spinner } from "@/components/ui/spinner";
import { AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * A unified loading wrapper that can show either a full page loader,
 * a specific skeleton, or just a spinner.
 *
 * @param {boolean} isLoading - Whether the content is currently loading
 * @param {any} error - Error object if an error occurred
 * @param {React.ReactNode} skeleton - Optional skeleton component to show while loading
 * @param {boolean} fullPage - Whether to show the full page loader (default: false)
 * @param {React.ReactNode} children - The content to show when not loading
 * @param {function} onRetry - Optional callback for retry button
 */
const LoadingWrapper = ({
  isLoading,
  error = null,
  skeleton = null,
  fullPage = false,
  children,
  onRetry = null,
}) => {
  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center p-12 w-full border rounded-lg bg-muted/50"
        role="alert"
        aria-live="assertive"
      >
        <AlertCircle className="h-12 w-12 text-destructive mb-3" />
        <h5 className="text-lg font-semibold text-foreground">
          Something went wrong
        </h5>
        <p className="text-muted-foreground text-center mb-6">
          {error.message || "Failed to load the requested information."}
          <br />
          {navigator.onLine ? "" : "Please check your internet connection."}
        </p>
        {onRetry && (
          <Button
            onClick={onRetry}
            aria-label="Retry loading content"
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Try Again
          </Button>
        )}
      </div>
    );
  }

  if (!isLoading) {
    return <>{children}</>;
  }

  if (fullPage) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
        aria-busy="true"
        aria-live="polite"
      >
        <Spinner size={48} />
      </div>
    );
  }

  if (skeleton) {
    return (
      <div aria-busy="true" aria-live="polite">
        {skeleton}
      </div>
    );
  }

  return (
    <div
      className="flex justify-center items-center p-12 w-full"
      aria-busy="true"
      aria-live="polite"
    >
      <Spinner size={32} />
      <span className="sr-only">Loading content...</span>
    </div>
  );
};

LoadingWrapper.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  skeleton: PropTypes.node,
  fullPage: PropTypes.bool,
  children: PropTypes.node,
  onRetry: PropTypes.func,
};

export default LoadingWrapper;
